// Sistema de cache offline con IndexedDB
import { openDB } from 'idb';

const DB_NAME = 'cenit-dashboard-cache';
const DB_VERSION = 1;

// Nombres de las stores de IndexedDB
const STORES = {
  API_RESPONSES: 'api-responses',
  STATIC_RESOURCES: 'static-resources',
  USER_DATA: 'user-data'
};

// Inicializar IndexedDB
const initDB = async () => {
  return openDB(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Store para respuestas de API
      if (!db.objectStoreNames.contains(STORES.API_RESPONSES)) {
        const apiStore = db.createObjectStore(STORES.API_RESPONSES, { keyPath: 'id' });
        apiStore.createIndex('endpoint', 'endpoint', { unique: false });
        apiStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store para recursos estáticos (imágenes, archivos)
      if (!db.objectStoreNames.contains(STORES.STATIC_RESOURCES)) {
        const staticStore = db.createObjectStore(STORES.STATIC_RESOURCES, { keyPath: 'url' });
        staticStore.createIndex('type', 'type', { unique: false });
        staticStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      // Store para datos de usuario
      if (!db.objectStoreNames.contains(STORES.USER_DATA)) {
        db.createObjectStore(STORES.USER_DATA, { keyPath: 'key' });
      }
    },
  });
};

// Clase para manejar cache de API
export class ApiCache {
  constructor() {
    this.dbPromise = initDB();
  }

  // Generar clave única para el cache basada en URL y parámetros
  generateCacheKey(method, url, params = null) {
    const baseKey = `${method}:${url}`;
    const paramString = params ? JSON.stringify(params) : '';
    return btoa(encodeURIComponent(baseKey + paramString)).slice(0, 100); // Limitar longitud
  }

  // Guardar respuesta de API en cache
  async set(method, url, params, response, ttl = 5 * 60 * 1000) { // 5 minutos por defecto
    try {
      const db = await this.dbPromise;
      const cacheKey = this.generateCacheKey(method, url, params);

      const cacheEntry = {
        id: cacheKey,
        method,
        url,
        params,
        response: {
          data: response.data,
          status: response.status,
          headers: response.headers,
          statusText: response.statusText
        },
        timestamp: Date.now(),
        ttl
      };

      await db.put(STORES.API_RESPONSES, cacheEntry);
    } catch (error) {
      console.warn('Error guardando en cache:', error);
    }
  }

  // Obtener respuesta de API del cache
  async get(method, url, params) {
    try {
      const db = await this.dbPromise;
      const cacheKey = this.generateCacheKey(method, url, params);
      const cached = await db.get(STORES.API_RESPONSES, cacheKey);

      if (!cached) return null;

      // Verificar si el cache ha expirado
      if (Date.now() - cached.timestamp > cached.ttl) {
        await this.delete(method, url, params);
        return null;
      }

      return {
        ...cached.response,
        cached: true,
        timestamp: cached.timestamp
      };
    } catch (error) {
      return null;
    }
  }

  // Verificar si existe cache válido
  async has(method, url, params) {
    const cached = await this.get(method, url, params);
    return cached !== null;
  }

  // Eliminar entrada del cache
  async delete(method, url, params) {
    try {
      const db = await this.dbPromise;
      const cacheKey = this.generateCacheKey(method, url, params);
      await db.delete(STORES.API_RESPONSES, cacheKey);
    } catch (error) {
      console.warn('Error eliminando del cache:', error);
    }
  }

  // Limpiar todo el cache de API
  async clear() {
    try {
      const db = await this.dbPromise;
      await db.clear(STORES.API_RESPONSES);
    } catch (error) {
      console.warn('Error limpiando cache:', error);
    }
  }

  // Limpiar cache expirado
  async clearExpired() {
    try {
      const db = await this.dbPromise;
      const tx = db.transaction(STORES.API_RESPONSES, 'readwrite');
      const store = tx.objectStore(STORES.API_RESPONSES);
      const index = store.index('timestamp');

      const now = Date.now();
      let cursor = await index.openCursor();

      while (cursor) {
        const entry = cursor.value;
        if (now - entry.timestamp > entry.ttl) {
          cursor.delete();
        }
        cursor = await cursor.continue();
      }

      await tx.done;
    } catch (error) {
      console.warn('Error limpiando cache expirado:', error);
    }
  }
}

// Clase para manejar cache de recursos estáticos
export class StaticCache {
  constructor() {
    this.dbPromise = initDB();
  }

  // Cachear recurso estático (imagen, archivo, etc.)
  async set(url, blob, type = 'unknown', ttl = 24 * 60 * 60 * 1000) { // 24 horas por defecto
    try {
      const db = await this.dbPromise;

      const cacheEntry = {
        url,
        blob,
        type,
        timestamp: Date.now(),
        ttl
      };

      await db.put(STORES.STATIC_RESOURCES, cacheEntry);
    } catch (error) {
      console.warn('Error guardando recurso estático:', error);
    }
  }

  // Obtener recurso estático del cache
  async get(url) {
    try {
      const db = await this.dbPromise;
      const cached = await db.get(STORES.STATIC_RESOURCES, url);

      if (!cached) return null;

      // Verificar si el cache ha expirado
      if (Date.now() - cached.timestamp > cached.ttl) {
        await this.delete(url);
        return null;
      }

      return cached.blob;
    } catch (error) {
      console.warn('Error obteniendo recurso estático:', error);
      return null;
    }
  }

  // Limpiar cache de recursos estáticos
  async clear() {
    try {
      const db = await this.dbPromise;
      await db.clear(STORES.STATIC_RESOURCES);
    } catch (error) {
      console.warn('Error limpiando cache estático:', error);
    }
  }
}

// Instancias singleton
export const apiCache = new ApiCache();
export const staticCache = new StaticCache();

// Utilidad para verificar conexión a internet
export const isOnline = () => {
  return navigator.onLine;
};

// Hook personalizado para datos offline (similar a SWR)
export const useOfflineData = (apiCall, dependencies = [], options = {}) => {
  const { ttl = 5 * 60 * 1000, enabled = true } = options;
};

// Función para limpiar todo el cache
export const clearAllCache = async () => {
  await apiCache.clear();
  await staticCache.clear();
};

// Función para obtener estadísticas del cache
export const getCacheStats = async () => {
  const db = await initDB();

  const apiCount = await db.count(STORES.API_RESPONSES);
  const staticCount = await db.count(STORES.STATIC_RESOURCES);

  return {
    apiResponses: apiCount,
    staticResources: staticCount,
    total: apiCount + staticCount
  };
};